"use client";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { Link } from "@/navigation.ts";
import dynamic from "next/dynamic";
import { type Blog } from "@/lib/@types/api/blog";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";
import { Books, Factory, FileText } from "@phosphor-icons/react";

// 动态导入博客组件
const LatestNews = dynamic(() => import("@/components/Blogs/BlogCard"), {
	loading: () => <div className="h-96 animate-pulse rounded-lg bg-gray-50"></div>,
});

export const NotFouned = ({ link, blogList }: { link?: string; blogList?: Blog.BlogListItem[] }) => {
	const t = useTranslations();
	const { openModalInquiry } = useModalInquiryContext();
	// 服务列表数据
	const services = [
		{
			slug: "5-axis-cnc-machining",
			name: t("NotFound.service5AxisCNC"),
			image: "/image/banner/new_part/CNC_Machining/CNC_Machining/CNC_Machining_Parts.png",
		},
		{
			slug: "plastic-injection-molding",
			name: t("NotFound.servicePlasticInjection"),
			image: "/image/banner/new_part/Injection_Molding/Plastic_injection_Molding/Plastic_injection_Parts.png",
		},
		{
			slug: "sheet-metal-fabrication",
			name: t("NotFound.serviceSheetMetal"),
			image:
				"/image/banner/new_part/Sheet_Metal_Fabricatlon/Sheet_Metal_Fabricatlon/Sheet_Metal_Fabricatlon_Parts.png",
		},
		{
			slug: "stamping",
			name: t("NotFound.serviceStamping"),
			image: "/image/banner/new_part/Sheet_Metal_Fabricatlon/Stamping/Stamping_Parts.png",
		},
		{
			slug: "metal-casting",
			name: t("NotFound.serviceMetalCasting"),
			image: "/image/banner/new_part/Die_Casting/Die_Casting/Die_Casting_Parts.png",
		},
		{
			slug: "3d-printing",
			name: t("NotFound.service3DPrinting"),
			image: "/image/banner/new_part/Solution/3D_Printing/3D-Printing_Parts.png",
		},
		{
			slug: "rapid-prototyping",
			name: t("NotFound.serviceRapidPrototyping"),
			image: "/image/banner/new_part/Solution/Rapid_Prototyping/Rapid-Prototyping_Parts.png",
		},
		{
			slug: "surface-finishing",
			name: t("NotFound.serviceSurfaceFinishing"),
			image: "/image/banner/new_part/Solution/Surface_Finishing/Surface_Finishing_Parts.png",
		},
	];

	// 快速操作数据
	const quickActions = [
		{
			href: "/upload",
			icon: <FileText size={28} weight="bold" className="text-[#65a525]" />,
			title: t("NotFound.getQuoteTitle"),
			description: t("NotFound.getQuoteDescription"),
		},
		{
			href: "/industry/automotive",
			icon: <Factory size={28} weight="bold" className="text-[#65a525]" />,
			title: t("NotFound.industriesTitle"),
			description: t("NotFound.industriesDescription"),
		},
		{
			href: "/resource",
			icon: <Books size={28} weight="bold" className="text-[#65a525]" />,
			title: t("NotFound.resourcesTitle"),
			description: t("NotFound.resourcesDescription"),
		},
	];

	return (
		<>
			<main className="container mx-auto max-w-[1000px] px-6 pb-4 pt-[60px]">
				{/* Error Section */}
				<div className="mb-12 text-center">
					<div className="mb-4 text-[120px] font-bold leading-none text-[#65a525] drop-shadow-[2px_2px_4px_rgba(0,102,204,0.1)]">
						404
					</div>
					<h1 className="mb-3 text-[32px] font-semibold text-[#1F2937]">{t("NotFound.title")}</h1>
					<p className="mx-auto mb-8 max-w-[600px] text-base text-[#6B7280]">{t("NotFound.description")}</p>
				</div>

				{/* Top CTA Buttons */}
				<div className="mx-auto mb-12 max-w-[600px]">
					<div className="flex flex-wrap justify-center gap-4">
						<Link
							href={link || "/"}
							className="rounded-[10px] bg-[#65a525] px-7 py-3.5 text-[15px] font-medium text-white transition-all hover:-translate-y-0.5 hover:bg-[#94c525] hover:text-[#000] hover:shadow-md"
						>
							{t("NotFound.returnToHomepage")}
						</Link>
						<button
							onClick={openModalInquiry}
							className="rounded-[10px] border-2 border-[#E5E7EB] bg-white px-7 py-3.5 text-[15px] font-medium text-[#1F2937] transition-all hover:border-[#65a525] hover:text-[#65a525]"
						>
							{t("NotFound.contactSupport")}
						</button>
					</div>
				</div>

				{/* Popular Manufacturing Services */}
				<div className="mb-12">
					<h2 className="mb-6 text-center text-xl font-semibold text-[#1F2937]">
						{t("NotFound.popularServices")}
					</h2>
					<div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-4">
						{services.map((service) => (
							<Link
								key={service.slug}
								href={`/${service.slug}`}
								className="flex flex-col overflow-hidden rounded-xl border border-[#E5E7EB] bg-white transition-all hover:-translate-y-1 hover:border-[#65a525] hover:shadow-md"
							>
								<div className="relative m-3 rounded-lg">
									<Image
										src={service.image}
										alt={service.name}
										width={300}
										height={300}
										className="mx-auto rounded-lg object-cover"
									/>
								</div>
								<div className="px-5 pb-4">
									<h3 className="m-0 text-center text-base font-semibold text-[#1F2937] group-hover:text-[#65a525]">
										{service.name}
									</h3>
								</div>
							</Link>
						))}
					</div>
				</div>

				{/* Quick Actions */}
				<div className="mb-12">
					<h2 className="mb-6 text-center text-xl font-semibold text-[#1F2937]">
						{t("NotFound.quickAccess")}
					</h2>
					<div className="grid grid-cols-1 gap-5 md:grid-cols-3">
						{quickActions.map((action, index) => (
							<Link
								key={index}
								href={action.href}
								className="block cursor-pointer rounded-xl border border-[#E5E7EB] bg-white p-6 text-center transition-all hover:-translate-y-0.5 hover:border-[#65a525] hover:shadow-md"
							>
								<div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-[rgba(0,204,10,0.1)] text-[28px]">
									{action.icon}
								</div>
								<div className="mb-2 text-lg font-semibold text-[#1F2937]">{action.title}</div>
								<div className="text-sm text-[#6B7280]">{action.description}</div>
							</Link>
						))}
					</div>
				</div>
			</main>

			{/* 最近博客模块 */}
			{blogList && blogList.length > 0 && (
				<section>
					<LatestNews
						blogList={blogList}
						title={t("blog.RelatedBlogs")}
						defaultTitle={t("home.latestnews")}
						defaultDescription={t("blog.dec")}
					/>
				</section>
			)}
		</>
	);
};
